import React, { useEffect, useRef, useState } from "react";
import { useParams, useSearchParams, Link } from "react-router-dom";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { api, API } from "@/lib/api";
import { labelFor } from "@/lib/categories";
import { IMAGES } from "@/lib/images";
import { StarsDisplay } from "@/components/Stars";
import { countryFlag, countryName } from "@/lib/countries";
import { useLang } from "@/context/LanguageContext";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { ArrowLeft, BadgeCheck, MapPin, PackageCheck, PackageSearch, MessageSquareQuote, ShieldCheck, Package, Facebook, MessageCircle, Link2, Image as ImageIcon } from "lucide-react";
import { XIcon } from "@/components/XIcon";
import { InstagramIcon, TikTokIcon } from "@/components/BrandIcons";
import { PageLoader } from "@/components/FindoSpinner";

const JSONLD_ID = "partenaire-jsonld";
const COVERS = [IMAGES.reception, IMAGES.conciergeDesk, IMAGES.hallway, IMAGES.heroLobby, IMAGES.receptionist, IMAGES.poolSunset];
const coverFor = (id) => {
  let h = 0;
  for (let i = 0; i < (id || "").length; i++) h = (h * 31 + id.charCodeAt(i)) % 997;
  return COVERS[h % COVERS.length];
};

function setMeta(name, content) {
  let el = document.querySelector(`meta[name="${name}"]`);
  if (!el) { el = document.createElement("meta"); el.setAttribute("name", name); document.head.appendChild(el); }
  el.setAttribute("content", content);
}
function setMetaProp(property, content) {
  let el = document.querySelector(`meta[property="${property}"]`);
  if (!el) { el = document.createElement("meta"); el.setAttribute("property", property); document.head.appendChild(el); }
  el.setAttribute("content", content);
}

const PIN = L.divIcon({
  className: "",
  html: `<div style="width:26px;height:26px;border-radius:50% 50% 50% 0;background:#1e3a5f;transform:rotate(-45deg);border:2px solid #fff;box-shadow:0 2px 6px rgba(0,0,0,.3)"></div>`,
  iconSize: [26, 26], iconAnchor: [13, 26],
});

function PartnerShareButtons({ partner, t }) {
  const [showPreview, setShowPreview] = useState(false);
  const eq = partner.establishment_id ? `?e=${partner.establishment_id}` : "";
  const url = `${window.location.origin}/partenaires/${partner.id}${eq}`;
  const text = `${partner.org_name} — Findloop`;
  const enc = encodeURIComponent;
  const links = {
    facebook: `https://www.facebook.com/sharer/sharer.php?u=${enc(url)}`,
    twitter: `https://twitter.com/intent/tweet?url=${enc(url)}&text=${enc(text)}`,
    whatsapp: `https://wa.me/?text=${enc(text + " " + url)}`,
  };
  const open = (href) => window.open(href, "_blank", "noopener,noreferrer,width=600,height=600");
  async function copy() {
    try { await navigator.clipboard.writeText(url); toast.success(t("an.linkCopied")); }
    catch { toast.error(t("an.copyFailed")); }
  }
  async function shareSocial(label) {
    if (navigator.share) {
      try { await navigator.share({ title: "findloop", text, url }); return; }
      catch (e) { if (e && e.name === "AbortError") return; }
    }
    try { await navigator.clipboard.writeText(url); toast.success(t("hc.linkCopiedShareOn").replace("{label}", label)); }
    catch { toast.error(t("an.copyFailed")); }
  }
  const btn = "h-9 w-9 grid place-items-center rounded-full border border-border text-slate-500 hover:text-findo-navy hover:border-findo-navy transition-colors";
  return (
    <div className="mt-5 border-t border-border pt-4" data-testid="partenaire-share">
      <div className="flex items-center gap-2">
        <span className="text-xs text-slate-400 font-medium mr-1">{t("mc.share")}</span>
        <button type="button" onClick={() => open(links.facebook)} className={btn} style={{ color: "#1877F2" }} title="Facebook" data-testid="partner-share-facebook"><Facebook size={16} /></button>
        <button type="button" onClick={() => open(links.twitter)} className={btn} style={{ color: "#000000" }} title="X / Twitter" data-testid="partner-share-twitter"><XIcon size={16} /></button>
        <button type="button" onClick={() => open(links.whatsapp)} className={btn} style={{ color: "#25D366" }} title="WhatsApp" data-testid="partner-share-whatsapp"><MessageCircle size={16} /></button>
        <button type="button" onClick={() => shareSocial("Instagram")} className={btn} title="Instagram" data-testid="partner-share-instagram"><InstagramIcon size={16} /></button>
        <button type="button" onClick={() => shareSocial("TikTok")} className={btn} title="TikTok" data-testid="partner-share-tiktok"><TikTokIcon size={16} /></button>
        <button type="button" onClick={copy} className={btn} title={t("an.copyLink")} data-testid="partner-share-copy"><Link2 size={16} /></button>
        <button type="button" onClick={() => setShowPreview((v) => !v)} className={`${btn} ml-auto ${showPreview ? "text-findo-navy border-findo-navy" : ""}`} title={t("hc.sharePreview")} data-testid="partner-share-preview-toggle">
          <ImageIcon size={16} />
        </button>
      </div>
      {showPreview && (
        <img
          src={`${API}/partners/${partner.id}/share-image.png`}
          alt="preview"
          className="mt-3 w-full rounded-xl border border-border animate-in fade-in duration-300"
          data-testid="partner-share-preview-img" />
      )}
    </div>
  );
}

function MiniMap({ lat, lng, name }) {
  const ref = useRef(null);
  const obj = useRef(null);
  useEffect(() => {
    if (obj.current || !ref.current) return;
    const map = L.map(ref.current, { scrollWheelZoom: false, dragging: true, zoomControl: true }).setView([lat, lng], 14);
    L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", { attribution: "&copy; OpenStreetMap", maxZoom: 19 }).addTo(map);
    L.marker([lat, lng], { icon: PIN }).addTo(map).bindPopup(name || "");
    obj.current = map;
    return () => { map.remove(); obj.current = null; };
  }, [lat, lng, name]);
  return <div ref={ref} className="w-full rounded-2xl overflow-hidden border border-border" style={{ height: 240, zIndex: 0 }} data-testid="partenaire-map" />;
}

export default function PartenaireDetail() {
  const { partnerId } = useParams();
  const [searchParams] = useSearchParams();
  const establishmentId = searchParams.get("e") || null;
  const eq = establishmentId ? `?e=${establishmentId}` : "";
  const { t } = useLang();
  const [partner, setPartner] = useState(null);
  const [reviews, setReviews] = useState(null);
  const [items, setItems] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    let alive = true;
    api(`/partners/${partnerId}/public${eq}`, { auth: false })
      .then((r) => { if (alive) setPartner(r); })
      .catch(() => { if (alive) setNotFound(true); });
    api(`/partners/${partnerId}/reviews${eq}`, { auth: false })
      .then((r) => { if (alive) setReviews(r.reviews || []); })
      .catch(() => { if (alive) setReviews([]); });
    api(`/partners/${partnerId}/recent-items${eq}`, { auth: false })
      .then((r) => { if (alive) setItems(r.items || []); })
      .catch(() => { if (alive) setItems([]); });
    return () => { alive = false; };
  }, [partnerId, eq]);

  useEffect(() => {
    if (!partner) return;
    const certified = partner.ratings?.certified;
    const title = `${partner.org_name} — ${t("par.title")} findloop | Findloop`;
    const desc = `${partner.org_name}, ${countryName(partner.country)} · ${partner.items_restituted || 0} ${t("pd.itemsReturned")}. ${certified ? t("pd.certified") + ". " : ""}${t("pd.declare")}.`;
    const pageUrl = `${window.location.origin}/partenaires/${partner.id}${partner.establishment_id ? `?e=${partner.establishment_id}` : ""}`;
    const shareImg = `${API}/partners/${partner.id}/share-image.png`;
    document.title = title;
    setMeta("description", desc);
    setMetaProp("og:type", "business.business");
    setMetaProp("og:title", title);
    setMetaProp("og:description", desc);
    setMetaProp("og:url", pageUrl);
    setMetaProp("og:image", shareImg);
    setMeta("twitter:card", "summary_large_image");
    setMeta("twitter:title", title);
    setMeta("twitter:description", desc);
    setMeta("twitter:image", shareImg);
    const jsonld = {
      "@context": "https://schema.org",
      "@type": "LocalBusiness",
      name: partner.org_name,
      url: pageUrl,
      address: { "@type": "PostalAddress", addressCountry: partner.country, addressLocality: partner.city || undefined },
      ...(partner.lat != null && partner.lng != null ? { geo: { "@type": "GeoCoordinates", latitude: partner.lat, longitude: partner.lng } } : {}),
      ...(partner.logo_base64 ? { logo: partner.logo_base64 } : {}),
      ...(partner.ratings?.count > 0
        ? { aggregateRating: { "@type": "AggregateRating", ratingValue: partner.ratings.average, reviewCount: partner.ratings.count } }
        : {}),
    };
    let el = document.getElementById(JSONLD_ID);
    if (!el) { el = document.createElement("script"); el.id = JSONLD_ID; el.type = "application/ld+json"; document.head.appendChild(el); }
    el.textContent = JSON.stringify(jsonld);
    return () => { const e = document.getElementById(JSONLD_ID); if (e) e.remove(); };
  }, [partner, t]);

  if (notFound) {
    return (
      <div className="max-w-2xl mx-auto px-6 py-24 text-center" data-testid="partenaire-notfound">
        <PackageSearch size={40} className="mx-auto text-slate-300" />
        <p className="mt-4 text-slate-500">{t("pd.notFound")}</p>
        <Link to="/partners"><Button variant="outline" className="mt-6 rounded-full">{t("pd.back")}</Button></Link>
      </div>
    );
  }
  if (!partner) return <PageLoader label={t("c.loading")} className="max-w-2xl mx-auto py-32" />;

  const certified = partner.ratings?.certified;
  const cover = partner.cover_base64 || coverFor(partner.id);
  const hasGeo = partner.lat != null && partner.lng != null;

  return (
    <div className="bg-background min-h-[70vh]" data-testid="partenaire-detail">
      {/* Cover banner */}
      <div className="relative h-64 md:h-72 overflow-hidden photo-scrim">
        <img src={cover} alt={partner.org_name} className="w-full h-full object-cover" />
        <span className={`absolute top-5 right-5 inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-bold uppercase tracking-wide ${certified ? "badge-red" : "bg-white/90 text-findo-navy"}`}>
          {certified ? <><BadgeCheck size={13} /> {t("pd.certified")}</> : t("par.partnerBadge")}
        </span>
      </div>

      <div className="max-w-5xl mx-auto px-6 -mt-12 relative z-10">
        <div className="bg-white rounded-3xl border border-border card-shadow p-6 md:p-8">
          <div className="flex items-start gap-5">
            <div className="h-20 w-20 rounded-2xl overflow-hidden bg-white border-4 border-white shadow-lg grid place-items-center shrink-0">
              {partner.logo_base64 ? <img src={partner.logo_base64} alt="" className="w-full h-full object-cover" /> : <span className="text-3xl">{countryFlag(partner.country)}</span>}
            </div>
            <div className="min-w-0 flex-1">
              <h1 className="font-display text-3xl md:text-4xl font-semibold text-findo-navy" data-testid="partenaire-name">{partner.org_name}</h1>
              <p className="text-sm text-slate-500 flex items-center gap-1.5 mt-1"><MapPin size={14} /> {partner.city ? `${partner.city}, ` : ""}{countryName(partner.country)}</p>
              <div className="mt-3 flex items-center gap-2">
                <StarsDisplay value={partner.ratings?.average ?? 0} size={16} />
                <span className="text-sm text-slate-400">{partner.ratings?.count ?? 0} {t("c.reviews")}</span>
              </div>
            </div>
          </div>

          {/* Trust stats */}
          <div className="mt-7 grid grid-cols-2 gap-4">
            <div className="rounded-2xl bg-findo-cream/60 p-5 text-center" data-testid="partenaire-restituted">
              <PackageCheck size={22} className="mx-auto text-findo-navy" />
              <p className="mt-2 font-display text-2xl font-semibold text-findo-navy">{partner.items_restituted || 0}</p>
              <p className="text-xs text-slate-500">{t("pd.itemsReturned")}</p>
            </div>
            <div className="rounded-2xl bg-findo-cream/60 p-5 text-center" data-testid="partenaire-total">
              <PackageSearch size={22} className="mx-auto text-findo-navy" />
              <p className="mt-2 font-display text-2xl font-semibold text-findo-navy">{partner.items_total || 0}</p>
              <p className="text-xs text-slate-500">{t("pd.itemsTotal")}</p>
            </div>
          </div>

          <Link to={`/declare/${partner.id}${eq}`}>
            <Button className="mt-7 w-full h-12 rounded-full font-semibold bg-findo-red hover:bg-findo-red/90 text-base" data-testid="partenaire-declare-btn">
              {t("pd.declare")}
            </Button>
          </Link>

          <PartnerShareButtons partner={partner} t={t} />
        </div>

        {/* Mini map */}
        {hasGeo && (
          <div className="mt-8" data-testid="partenaire-map-section">
            <h2 className="font-display text-2xl font-semibold text-findo-navy mb-4 flex items-center gap-2"><MapPin size={20} className="text-findo-red" /> {t("pd.map")}</h2>
            <MiniMap lat={partner.lat} lng={partner.lng} name={partner.org_name} />
          </div>
        )}

        {/* Recent found items */}
        <div className="mt-8" data-testid="partenaire-items">
          <h2 className="font-display text-2xl font-semibold text-findo-navy mb-4 flex items-center gap-2"><Package size={20} className="text-findo-red" /> {t("pd.recentItems")}</h2>
          {items === null ? (
            <div className="grid sm:grid-cols-3 gap-4">{Array.from({ length: 3 }).map((_, i) => <div key={i} className="h-40 rounded-2xl bg-slate-100 animate-pulse" />)}</div>
          ) : items.length === 0 ? (
            <p className="text-slate-400 text-sm">{t("pd.noItems")}</p>
          ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {items.map((it) => (
                <div key={it.id} className="bg-white rounded-2xl border border-border card-shadow overflow-hidden" data-testid="partenaire-item">
                  <div className="h-32 bg-slate-100">
                    {it.image_base64 ? <img src={it.image_base64} alt="" className="w-full h-full object-cover" /> : <div className="w-full h-full grid place-items-center text-slate-300"><PackageSearch size={28} /></div>}
                  </div>
                  <div className="p-4">
                    <p className="text-xs text-findo-red font-semibold">{labelFor(it.category)}</p>
                    <p className="text-sm font-semibold text-slate-800 truncate">{it.description}</p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Reviews */}
        <div className="mt-8 mb-14" data-testid="partenaire-reviews">
          <h2 className="font-display text-2xl font-semibold text-findo-navy mb-4">{t("pd.reviews")}</h2>
          {reviews === null ? (
            <PageLoader label={t("c.loading")} className="py-8" size={32} />
          ) : reviews.length === 0 ? (
            <p className="text-slate-400 text-sm">{t("pd.noReviews")}</p>
          ) : (
            <div className="grid sm:grid-cols-2 gap-4">
              {reviews.map((r) => (
                <div key={r.id} className="bg-white rounded-2xl border border-border card-shadow p-5" data-testid="partenaire-review">
                  <div className="flex items-center justify-between gap-2">
                    <StarsDisplay value={r.rating ?? 0} size={14} />
                    {r.verified && (
                      <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 text-emerald-700 px-2 py-0.5 text-[11px] font-bold" data-testid="review-verified-badge">
                        <ShieldCheck size={12} /> {t("pd.verified")}
                      </span>
                    )}
                  </div>
                  {r.title && <p className="mt-2 font-semibold text-findo-navy">{r.title}</p>}
                  {r.body && <p className="mt-1 text-sm text-slate-600">{r.body}</p>}
                  <p className="mt-3 text-xs text-slate-400 font-medium flex items-center gap-1.5" data-testid="review-author">
                    <MessageSquareQuote size={13} className="text-findo-red" /> {r.author_name || "Client findloop"}
                  </p>
                  {r.partner_reply?.body && (
                    <div className="mt-3 rounded-xl bg-findo-cream/60 p-3 border-l-2 border-findo-red">
                      <p className="text-[11px] font-bold uppercase tracking-wide text-findo-navy">{t("pd.reply")}</p>
                      <p className="mt-1 text-sm text-slate-600">{r.partner_reply.body}</p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
